import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { validateEmail, validateOtpCode } from './validation';
import { t } from '../../utils/i18n';

/**
 * OTP login state machine:
 * idle -> sending (pošalji kod) -> sent (unos koda) -> verifying -> success/error
 */
const OT_STATE = { idle: 'idle', sending: 'sending', sent: 'sent', verifying: 'verifying' };

export default function OtpLoginForm({ onSendCode, onVerifyCode, onSuccess, setError }) {
  const [state, setState] = useState(OT_STATE.idle);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const codeInputRef = useRef(null);

  useEffect(() => {
    if (state === OT_STATE.sent) {
      codeInputRef.current?.focus();
    }
  }, [state]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    setSubmitError('');
    setError('');

    const res = validateEmail(email);
    if (!res.valid) {
      setFieldErrors({ email: res.message });
      return;
    }

    setState(OT_STATE.sending);
    const result = await onSendCode(email);
    if (result.success) {
      setState(OT_STATE.sent);
      setResendCooldown(60);
    } else {
      setState(OT_STATE.idle);
      setSubmitError(result.error);
    }
  };

  const runVerify = async (codeToVerify) => {
    const res = validateOtpCode(codeToVerify);
    if (!res.valid) {
      setFieldErrors({ code: res.message });
      return;
    }
    setFieldErrors({});
    setSubmitError('');
    setError('');
    setState(OT_STATE.verifying);
    const result = await onVerifyCode(email, codeToVerify);
    if (result.success) {
      onSuccess(result);
    } else {
      setState(OT_STATE.sent);
      setSubmitError(result.error);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    await runVerify(code);
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setFieldErrors({});
    setSubmitError('');
    const result = await onSendCode(email);
    if (result.success) {
      setResendCooldown(60);
    } else {
      setSubmitError(result.error);
    }
  };

  const handleChangeEmail = () => {
    setState(OT_STATE.idle);
    setCode('');
    setFieldErrors({});
    setSubmitError('');
  };

  return (
    <div className="space-y-4">
      {state === OT_STATE.idle || state === OT_STATE.sending ? (
        <form onSubmit={handleSendCode} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1.5 text-gray-900 dark:text-gray-100">Email adresa</label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setFieldErrors((prev) => ({ ...prev, email: null }));
              }}
              required
              autoComplete="email"
              className={`input-base ${fieldErrors.email ? 'input-error' : ''}`}
              placeholder={t('auth.login.emailPlaceholder') || 'vas@email.com'}
            />
            {fieldErrors.email && <p className="error-text">{fieldErrors.email}</p>}
          </div>

          {submitError && (
            <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
              {submitError}
            </div>
          )}

          <button
            type="submit"
            disabled={state === OT_STATE.sending || !email}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {state === OT_STATE.sending ? (
              <>
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                Šalje se...
              </>
            ) : (
              'Pošalji kod'
            )}
          </button>
        </form>
      ) : (
        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1.5 text-gray-900 dark:text-gray-100">Kod (6 cifara)</label>
            <input
              ref={codeInputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={code}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                setCode(val);
                if (val.length === 6) {
                  setTimeout(() => runVerify(val), 50);
                }
              }}
              maxLength={6}
              placeholder="000000"
              className={`input-base text-center text-2xl tracking-[0.4em] ${fieldErrors.code ? 'input-error' : ''}`}
            />
            <p className="helper-text text-center">{t('auth.login.otpSentTo') || 'Poslato na'} {email}</p>
            {fieldErrors.code && <p className="error-text">{fieldErrors.code}</p>}
          </div>

          {submitError && (
            <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
              {submitError}
            </div>
          )}

          <button
            type="submit"
            disabled={state === OT_STATE.verifying || code.length !== 6}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {state === OT_STATE.verifying ? (
              <>
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                Verifikacija...
              </>
            ) : (
              'Potvrdi i prijavi se'
            )}
          </button>

          <div className="flex flex-wrap gap-4 justify-center text-sm">
            <button
              type="button"
              onClick={handleResend}
              disabled={resendCooldown > 0}
              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Pošalji ponovo {resendCooldown > 0 && `(${resendCooldown}s)`}
            </button>
            <button
              type="button"
              onClick={handleChangeEmail}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              Promeni email
            </button>
          </div>
        </form>
      )}

      <p className="text-sm text-center text-gray-600 dark:text-gray-400">
        Nemate nalog?{' '}
        <Link
          to="/register"
          className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold"
        >
          Registrujte se
        </Link>
      </p>
    </div>
  );
}
